import type { SittingGroup } from '../lib/sitting';
import type { AnswerState } from './useSitting';
import { EssayControl } from './EssayControl';
import { Rubric, statesWordLimit } from './Rubric';
import { BankPanel, DropZone } from './drag';
import { MatchGrid } from './MatchGrid';
import { headingSlots } from './headings';
import { isRomanBank } from './letters';
import {
  BankSelect,
  ChoiceControl,
  FixedLegend,
  GapBody,
  GapInput,
  TextControl,
  type AnswerProps,
} from './AnswerControls';

interface Props {
  group: SittingGroup;
  answers: Map<number, AnswerState>;
  current: number;
  disabled: boolean;
  onChange: (number: number, patch: Partial<AnswerState>) => void;
  onFocusQuestion: (number: number) => void;
  /**
   * True when this group's heading slots are drawn on the passage in the other
   * pane, so the questions are not repeated here.
   */
  headingsInPassage?: boolean;
  /**
   * Which paper this is. A Listening paper draws a bank as plain cards beside
   * numbered gaps, and a Reading paper letters them — which is how the real
   * screens draw each, and the difference is worth carrying.
   */
  module?: 'LISTENING' | 'READING' | 'WRITING';
}

/** The badges beside True/False/Not Given, which are answers A, B and C. */
const BADGES = ['A', 'B', 'C', 'D', 'E'];

/** One instruction, one body or bank, and the questions that share them. */
export function ExamGroup({
  group,
  answers,
  current,
  disabled,
  onChange,
  onFocusQuestion,
  headingsInPassage = false,
  module = 'READING',
}: Props) {
  const propsFor = (number: number, spanTo?: number | null): AnswerProps => ({
    number,
    answer: answers.get(number),
    disabled,
    // A prompt worth several marks is one answer to the candidate, so the
    // selection is written to every number it covers. Marking then reads it
    // from whichever number survived, and the numbered strip along the bottom
    // shows the whole span as answered rather than only its first mark.
    onChange: (patch) => {
      for (let n = number; n <= (spanTo ?? number); n += 1) onChange(n, patch);
    },
    onFocus: () => onFocusQuestion(number),
  });

  // Completion types put their answers inside the body text, so the questions
  // are not listed separately — exactly as the paper is printed.
  const answersLiveInBody = Boolean(group.bodyHtml) && group.inputMode !== 'CHOICE_ONE';

  const bank = group.optionsBank ?? [];
  const bankByLetter = new Map(bank.map((option) => [option.letter, option]));
  const usedLetters = new Set(
    group.questions.map((q) => answers.get(q.number)?.value?.toUpperCase()).filter(Boolean) as string[],
  );

  /**
   * Whether a card leaves the bank when it is placed.
   *
   * It does not where the same letter answers more than one question: the
   * rubric says so in as many words, and a bank with fewer cards than
   * questions has no other way to fill them.
   */
  const bankReused =
    bank.length < group.questions.length ||
    /more than once|more than one/i.test(group.instructions ?? '');

  const roman = isRomanBank(bank);
  /** A Listening paper's gaps hold their number and its cards carry no letter. */
  const spoken = module === 'LISTENING';
  /**
   * Unless the rubric asks for one.
   *
   * "Choose FOUR answers from the box and write the correct letter, A-F" is a
   * task whose answer IS the letter, and a Listening paper drew those cards
   * plain — statements in a box with nothing to write next to a question. The
   * rubric is the only thing that knows the difference between dragging a word
   * into a gap and naming a letter, so it is what decides.
   */
  const lettersAsked = /\bletters?\b/i.test(group.instructions ?? '');
  const isDrag = group.inputMode === 'DRAG';
  const isGrid = group.inputMode === 'GRID';
  const slots = isDrag ? headingSlots(group) : null;
  /**
   * Sentence endings: the blank ends each sentence and the endings are listed
   * underneath them, which is the one drag task whose bank comes after the
   * questions rather than before.
   */
  const isEndings = isDrag && !group.bodyHtml && !slots;
  /**
   * Listening's matching task puts the box of answers beside the list rather
   * than under it — the labels are short, so there is room. Sentence endings
   * are whole clauses and keep theirs underneath, as the paper prints both.
   */
  const bankBeside = isEndings && group.type === 'MATCHING';
  /**
   * Map and diagram labelling: the numbers are printed on the artwork itself,
   * as they are in the real test, and the boxes are listed underneath it. With
   * no body to hold them, these questions had nowhere to draw an input at all
   * — the group could be looked at but not answered.
   */
  /**
   * Whether the gaps live in the group's text.
   *
   * A completion task puts them there and the question list is only a summary;
   * a labelling task has no text and draws a box per question. The body's
   * mere existence used to decide it, which is not the same thing: a paper
   * whose map arrived with a stray paragraph attached had a body, no gaps in
   * it, and so drew neither — the numbers and the words of every question,
   * and nowhere to write. Asking whether the text actually holds the gaps is
   * the question that was meant.
   */
  const bodyHasGaps = /data-gap="/.test(group.bodyHtml ?? '');
  const isLabelling = Boolean(group.imageUrl) && !bodyHasGaps && !isDrag && !isGrid;
  /**
   * A labelling paper that places its own gaps on the picture.
   *
   * Where the paper says where each number goes, the gap is drawn there — a
   * candidate reads the plan the way they will on the day. Where it does not,
   * the numbers were printed into the artwork when it was made and the gaps
   * are listed underneath, which is the older way and still the common one.
   */
  const placed = Boolean(group.imageUrl) && group.questions.every((q) => q.at);

  const bankPanel =
    isDrag && bank.length > 0 ? (
      <BankPanel
        groupId={group.id}
        title={slots ? 'List of Headings' : undefined}
        hint={
          spoken
            ? ''
            : slots
              ? headingsInPassage
                ? 'Drag and drop the headings to the correct paragraphs in the passage.'
                : 'Drag and drop each heading onto the paragraph it belongs to.'
              : 'Drag and drop an option to fill in each blank.'
        }
        options={bank}
        used={usedLetters}
        oneUse={!bankReused}
        disabled={disabled}
        layout={spoken ? 'plain' : slots ? 'rows' : isEndings ? 'rows' : 'chips'}
        showLetters={lettersAsked || !spoken}
      />
    ) : null;

  /**
   * A Writing task shows only the box. Its prompt is rendered beside it, in
   * the other pane, so repeating it here would put the same paragraph on
   * screen twice.
   */
  if (group.inputMode === 'ESSAY') {
    const question = group.questions[0];
    if (!question) return null;
    return (
      <section className="qgroup" aria-label={`Task ${group.fromQ}`}>
        <EssayControl {...propsFor(question.number)} />
      </section>
    );
  }

  return (
    <section className="qgroup" aria-label={`Questions ${group.fromQ} to ${group.toQ}`}>
      <header className="qgroup__head">
        <h3 className="qgroup__title">
          {group.fromQ === group.toQ
            ? `Question ${group.fromQ}`
            : `Questions ${group.fromQ}–${group.toQ}`}
        </h3>
        {group.instructions && <Rubric text={group.instructions} />}
        {group.maxWords && !statesWordLimit(group.instructions) && (
          <p className="xrubric" data-markable>
            Write <strong>NO MORE THAN {numberWord(group.maxWords)} WORD
            {group.maxWords === 1 ? '' : 'S'}</strong> for each answer.
          </p>
        )}
      </header>

      {group.inputMode === 'FIXED' && <FixedLegend type={group.type} />}

      {group.imageUrl && !isGrid && !placed && (
        <figure className="qgroup__figure">
          <img src={group.imageUrl} alt={`Diagram for questions ${group.fromQ} to ${group.toQ}`} />
        </figure>
      )}

      {placed && (
        <div className="qplan">
          <figure className="qplan__art">
            <img src={group.imageUrl!} alt={`Plan for questions ${group.fromQ} to ${group.toQ}`} />
            {group.questions.map((question) => (
              <span
                key={question.id}
                className="qplan__slot"
                style={{ left: `${question.at!.x}%`, top: `${question.at!.y}%` }}
              >
                <DropZone
                  groupId={group.id}
                  option={bankByLetter.get(
                    (answers.get(question.number)?.value ?? '').toUpperCase(),
                  )}
                  roman={roman}
                  placeholder={null}
                  current={question.number === current}
                  {...propsFor(question.number)}
                />
              </span>
            ))}
          </figure>
          {bankPanel}
        </div>
      )}

      {/*
        * The cards to drag. A Listening paper stands them beside whatever they
        * are dropped into — the chart, the notes, the list of names — so they
        * are rendered with it below rather than above it here.
        */}
      {!isEndings && !placed && !(spoken && group.bodyHtml) && bankPanel}

      {/* A grid's columns are its letters; any text they carry is listed above it. */}
      {isGrid && bank.some((option) => option.text) && (
        <ul className="blist">
          {bank.map((option) => (
            <li key={option.letter}>
              <span className="blist__letter">{option.letter}</span>
              <span data-markable>{option.text}</span>
            </li>
          ))}
        </ul>
      )}

      {group.inputMode === 'BANK' && group.optionsBank && (
        <ul className="bank">
          {group.optionsBank.map((option) => (
            <li key={option.letter}>
              <span className="bank__letter mono">{option.letter}</span>
              {/* Wrapped rather than left bare: a highlight is a <mark> around
                  part of a text node, and it must sit inside an element React
                  is not inserting siblings into. */}
              <span data-markable>{option.text}</span>
            </li>
          ))}
        </ul>
      )}

      {group.bodyHtml && (
        <div className={spoken && bankPanel ? 'qbeside' : undefined}>
        <div className="qgroup__panel">
          <GapBody
            html={group.bodyHtml}
            renderGap={(n) =>
              isDrag ? (
                <DropZone
                  groupId={group.id}
                  option={bankByLetter.get((answers.get(n)?.value ?? '').toUpperCase())}
                  roman={roman}
                  placeholder={spoken ? null : 'Drop answer here'}
                  current={n === current}
                  {...propsFor(n)}
                />
              ) : (
                <GapInput group={group} numberInside={spoken} {...propsFor(n)} />
              )
            }
          />
        </div>
        {spoken && bankPanel}
        </div>
      )}

      {/* Headings dropped onto the passage are answered in the other pane. */}
      {isGrid ? (
        <div className={group.imageUrl ? 'qlabel' : undefined}>
          {group.imageUrl && (
            <figure className="qgroup__figure qlabel__art">
              <img
                src={group.imageUrl}
                alt={`Artwork for questions ${group.fromQ} to ${group.toQ}`}
              />
            </figure>
          )}
          <MatchGrid
            letters={bank.map((option) => option.letter)}
            rows={group.questions.map((question) => ({
              promptHtml: question.promptHtml,
              ...propsFor(question.number, question.spanTo),
            }))}
            current={current}
            disabled={disabled}
          />
        </div>
      ) : slots && headingsInPassage ? null : slots ? (
        <ul className="xslots">
          {group.questions.map((question) => (
            <li key={question.id} id={`q-${question.number}`}>
              <span className="xslots__label">
                {question.promptHtml ? (
                  <span data-markable dangerouslySetInnerHTML={{ __html: question.promptHtml }} />
                ) : (
                  `Question ${question.number}`
                )}
              </span>
              <DropZone
                groupId={group.id}
                option={bankByLetter.get((answers.get(question.number)?.value ?? '').toUpperCase())}
                roman={roman}
                placeholder={spoken ? null : 'Drop heading here'}
                current={question.number === current}
                {...propsFor(question.number)}
              />
            </li>
          ))}
        </ul>
      ) : placed ? null : isLabelling ? (
        <ul className="xlabels">
          {group.questions.map((question) => (
            <li key={question.id} id={`q-${question.number}`}>
              <GapInput group={group} numberInside={spoken} {...propsFor(question.number)} />
              {question.promptHtml && (
                <span data-markable dangerouslySetInnerHTML={{ __html: question.promptHtml }} />
              )}
            </li>
          ))}
        </ul>
      ) : isEndings ? (
        <div className={bankBeside ? 'qmatch' : undefined}>
          <ul className="xends">
            {group.questions.map((question) => (
              <li
                key={question.id}
                id={`q-${question.number}`}
                data-current={question.number === current || undefined}
              >
                {question.promptHtml ? (
                  <span data-markable dangerouslySetInnerHTML={{ __html: question.promptHtml }} />
                ) : (
                  <span className="muted">—</span>
                )}
                <DropZone
                  groupId={group.id}
                  option={bankByLetter.get(
                    (answers.get(question.number)?.value ?? '').toUpperCase(),
                  )}
                  roman={roman}
                  placeholder={spoken ? null : 'Drop answer here'}
                  current={question.number === current}
                  {...propsFor(question.number)}
                />
              </li>
            ))}
          </ul>
          {bankPanel}
        </div>
      ) : (
        !answersLiveInBody &&
        group.questions
          // Only the first number of a span draws the prompt; the rest are the
          // same question and would otherwise be printed again under each mark.
          .filter((q) => q.spanFrom == null || q.spanFrom === q.number)
          .map((question) => {
            const props = propsFor(question.number, question.spanTo);
            return (
              <div
                key={question.id}
                id={`q-${question.number}`}
                className={`xquestion${question.number === current ? ' xquestion--current' : ''}`}
              >
                <div className="xquestion__stem">
                  {/* "Choose TWO letters" is worth two marks, and the real
                      screen boxes both numbers side by side rather than
                      printing the range. */}
                  <span className="xnums">
                    {numbersOf(question.number, question.spanTo).map((n) => (
                      <span key={n} className="xnum">
                        {n}
                      </span>
                    ))}
                  </span>
                  <span style={{ flex: 1 }}>
                    {question.promptHtml ? (
                      <span data-markable dangerouslySetInnerHTML={{ __html: question.promptHtml }} />
                    ) : (
                      <span className="muted">—</span>
                    )}
                    {group.inputMode === 'BANK' && (
                      <span style={{ marginLeft: '0.75rem' }}>
                        <BankSelect group={group} {...props} />
                      </span>
                    )}
                    {/*
                      * A typed gap with nowhere to live.
                      *
                      * A completion group draws its gaps inside its text. When
                      * the text holds none — a paper that lost its markers, a
                      * group that should never have had a body — this list was
                      * the last place left and it drew nothing at all: every
                      * question present, numbered, worded, and unanswerable.
                      */}
                    {group.inputMode === 'GAP' && !bodyHasGaps && (
                      <span style={{ marginLeft: '0.75rem' }}>
                        <GapInput group={group} {...props} />
                      </span>
                    )}
                    {group.inputMode === 'TEXT' && (
                      <span style={{ marginLeft: '0.75rem' }}>
                        <TextControl {...props} />
                      </span>
                    )}
                  </span>
                </div>

                {(group.inputMode === 'CHOICE_ONE' || group.inputMode === 'CHOICE_MANY') && (
                  <ChoiceControl
                    group={group}
                    options={question.options ?? []}
                    limit={question.spanTo != null ? question.spanTo - question.number + 1 : undefined}
                    {...props}
                  />
                )}
                {group.inputMode === 'FIXED' && (
                  <ChoiceControl
                    group={group}
                    options={(group.fixedOptions ?? []).map((text, i) => ({
                      letter: BADGES[i] ?? String(i + 1),
                      text,
                      value: text,
                    }))}
                    {...props}
                  />
                )}
              </div>
            );
          })
      )}
    </section>
  );
}

/** Every number one prompt covers: [23] and [24], not "23-24". */
function numbersOf(from: number, to: number | null | undefined): number[] {
  const out: number[] = [];
  for (let n = from; n <= (to && to > from ? to : from); n += 1) out.push(n);
  return out;
}

const WORDS = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'];
const numberWord = (n: number) => WORDS[n] ?? String(n);
